import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Divider, Button, TextField, Avatar } from '@mui/material';
import { getCardById } from 'apis/cardApi';
import { createLead } from 'apis/leadCaptureApi';
import { useParams } from 'react-router-dom';
import { AddIcon } from 'icons/materialIcons';
import { darken } from '@mui/system';
import { validateEmail, validatePhoneNumber } from 'utils/validator';
import { useAuth } from 'contexts/AuthContext';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import { useEventTracking } from 'services/eventTrackingService';
import PhoneInputField from 'components/common/PhoneInputField';
import { useCardContext } from 'contexts/CardContext';

const LeadCaptureOverlay = ({
  onClose,
  onSuccess,
  addMoreConnectButtonColor = '#2f82ed',
  cardLayoutType,
  triggerSource = 'auto'
}) => {
  const tracking = useEventTracking();
  const { cardState } = useCardContext();
  const [submitting, setSubmitting] = useState(false);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const { cardId } = useParams();
  const [cardDetails, setCardDetails] = useState({});
  const [cardProfilePictureUrl, setCardProfilePictureUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [cardFullName, setCardFullName] = useState('');
  const [cardEmail, setCardEmail] = useState('');
  const [inputFieldsConfig, setInputFieldsConfig] = useState([
    { label: 'Full Name', required: false, type: 'text' },
    { label: 'Email', required: true, type: 'text' },
    { label: 'Phone Number', required: false, type: 'text' },
    { label: 'Job Title', required: false, type: 'text' },
    { label: 'Company', required: false, type: 'text' },
    { label: 'Note', required: false, type: 'text' }
  ]);
  const { authState } = useAuth();
  
  // Dynamic form state for additional fields
  const [dynamicFieldValues, setDynamicFieldValues] = useState({});
  const [dynamicFieldErrors, setDynamicFieldErrors] = useState({});

  // FIXED: Safely get userId, default to card owner's userId for unauthenticated users
  const userId = authState?.user?.userId || cardDetails?.card?.userId || null;
  const isAuthenticated = authState?.isAuthenticated || false;

  // Form data state
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadPhoneNumber, setLeadPhoneNumber] = useState('');
  const [leadJobTitle, setLeadJobTitle] = useState('');
  const [leadCompany, setLeadCompany] = useState('');
  const [leadNotes, setLeadNotes] = useState('');

  // Error states
  const [leadNameError, setLeadNameError] = useState('');
  const [leadEmailError, setLeadEmailError] = useState('');
  const [leadPhoneNumberError, setLeadPhoneNumberError] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);

  // DYNAMIC modal position that adjusts when more fields are shown
  const MODAL_POSITION = triggerSource === 'auto' ? {
    top: {
      xs: showMoreFields ? '5vh' : '18vh',
      sm: showMoreFields ? '8vh' : '22vh',
      md: showMoreFields ? '10vh' : '25vh'
    },
    transform: 'translate(-50%, 0)',
  } : {
    top: {
      xs: showMoreFields ? '15%' : '35%',
      sm: showMoreFields ? '20%' : '40%',
      md: showMoreFields ? '25%' : '45%'
    },
    transform: 'translate(-50%, -50%)',
  };

  const BASE_URL = `${FRONTEND_BASE_URL}/card`;
  const cardUrl = `${BASE_URL}/${cardId}`;

  // Helper function to get field config by default label
  const getFieldConfigByDefaultLabel = useCallback((defaultLabel) => {
    const defaultLabelVariations = {
      'Full Name': ['Full Name', 'Name', 'fullName'],
      'Email': ['Email', 'email'],
      'Phone Number': ['Phone Number', 'Phone', 'phoneNumber'],
      'Job Title': ['Job Title', 'Title', 'jobTitle'],
      'Company': ['Company', 'company'],
      'Note': ['Note', 'Notes', 'note', 'Notes On This Interaction']
    };
    
    const variations = defaultLabelVariations[defaultLabel] || [defaultLabel];
    
    for (const variation of variations) {
      const found = inputFieldsConfig.find(f => 
        f.label === variation || 
        f.label.toLowerCase() === variation.toLowerCase()
      );
      if (found) return found;
    }
    
    const defaultLabelMap = {
      'Full Name': 0,
      'Email': 1,
      'Phone Number': 2,
      'Job Title': 3,
      'Company': 4,
      'Note': 5
    };
    const index = defaultLabelMap[defaultLabel];
    if (index !== undefined && inputFieldsConfig[index]) {
      const fieldAtPosition = inputFieldsConfig[index];
      const matchesDefault = variations.some(v => 
        fieldAtPosition.label === v || 
        fieldAtPosition.label.toLowerCase() === v.toLowerCase()
      );
      if (matchesDefault) {
        return fieldAtPosition;
      }
    }
    
    return undefined;
  }, [inputFieldsConfig]);

  const fieldExists = useCallback((defaultLabel) => {
    return getFieldConfigByDefaultLabel(defaultLabel) !== undefined;
  }, [getFieldConfigByDefaultLabel]);

  const isFieldRequired = useCallback((defaultLabel) => {
    const field = getFieldConfigByDefaultLabel(defaultLabel);
    return field ? field.required : false;
  }, [getFieldConfigByDefaultLabel]);

  const getFieldLabel = useCallback((defaultLabel) => {
    const field = getFieldConfigByDefaultLabel(defaultLabel);
    return field ? field.label : defaultLabel;
  }, [getFieldConfigByDefaultLabel]);

  // Get all fields that should be rendered (excluding standard fields)
  const getAdditionalFields = useCallback(() => {
    if (!inputFieldsConfig || inputFieldsConfig.length === 0) return [];
    
    const standardFieldLabels = new Set();
    ['Full Name', 'Email', 'Phone Number', 'Job Title', 'Company', 'Note'].forEach(defaultLabel => {
      const field = getFieldConfigByDefaultLabel(defaultLabel);
      if (field) {
        standardFieldLabels.add(field.label);
      }
    });

    const additionalFields = inputFieldsConfig.filter(field => {
      const isTextField = field.type === 'text' || !field.type;
      const isNotStandard = !standardFieldLabels.has(field.label);
      return isTextField && isNotStandard;
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 getAdditionalFields:', {
        totalFields: inputFieldsConfig.length,
        standardFieldLabels: Array.from(standardFieldLabels),
        additionalFields: additionalFields.map(f => ({ label: f.label, type: f.type }))
      });
    }

    return additionalFields;
  }, [inputFieldsConfig, getFieldConfigByDefaultLabel]);

  // Handle dynamic field changes
  const handleDynamicFieldChange = useCallback((fieldLabel, value) => {
    setDynamicFieldValues(prev => ({
      ...prev,
      [fieldLabel]: value
    }));

    if (formSubmitted) {
      const field = inputFieldsConfig.find(f => f.label === fieldLabel);
      if (field && field.required && !value.trim()) {
        setDynamicFieldErrors(prev => ({
          ...prev,
          [fieldLabel]: `${fieldLabel} is required`
        }));
      } else {
        setDynamicFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldLabel];
          return newErrors;
        });
      }
    }
  }, [formSubmitted, inputFieldsConfig]);

  // STABLE event handlers
  const handleAddMoreClick = useCallback(() => {
    setShowMoreFields(true);
  }, []);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    switch (name) {
      case 'name':
        setLeadName(value);
        if (formSubmitted && !value.trim()) {
          setLeadNameError('Name cannot be empty');
        } else {
          setLeadNameError('');
        }
        break;
      case 'email':
        setLeadEmail(value);
        if (formSubmitted) {
          if (!value) {
            setLeadEmailError('Email is required');
          } else if (!validateEmail(value)) {
            setLeadEmailError('Invalid email address!');
          } else {
            setLeadEmailError('');
          }
        }
        break;
      case 'jobTitle':
        setLeadJobTitle(value);
        break;
      case 'company':
        setLeadCompany(value);
        break;
      case 'notes':
        setLeadNotes(value);
        break;
      default:
        break;
    }
  }, [formSubmitted]);

  const handlePhoneChange = useCallback((value) => {
    setLeadPhoneNumber(value);
    if (value && !validatePhoneNumber(value)) {
      setLeadPhoneNumberError('Please enter a valid phone number');
    } else {
      setLeadPhoneNumberError('');
    }
  }, []);

  // STABLE validateForm function
  const validateForm = useCallback(() => {
    let isValid = true;

    if (!leadEmail) {
      setLeadEmailError('Email is required');
      isValid = false;
    } else if (!validateEmail(leadEmail)) {
      setLeadEmailError('Invalid email address!');
      isValid = false;
    } else {
      setLeadEmailError('');
    }

    if (leadName && leadName.trim().length === 0) {
      setLeadNameError('Name cannot be empty');
      isValid = false;
    } else {
      setLeadNameError('');
    }

    if (leadPhoneNumber && !validatePhoneNumber(leadPhoneNumber)) {
      setLeadPhoneNumberError('Please enter a valid phone number');
      isValid = false;
    } else {
      setLeadPhoneNumberError('');
    }

    return isValid;
  }, [leadEmail, leadName, leadPhoneNumber]);

  // STABLE handleSubmit - FIXED to work with unauthenticated users
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    if (!validateForm()) {
      return;
    }

    // Don't proceed if we don't have a userId (neither authenticated user nor card owner)
    if (!userId) {
      console.error('No user ID available for lead capture');
      return;
    }

    setSubmitting(true);

    const cardData = { cardUrl, cardEmail };

    try {
      const leadResponse = await createLead(
        {
          leadName,
          leadEmail,
          leadPhoneNumber,
          leadJobTitle,
          leadCompany,
          leadNotes,
          cardId: cardId,
          leadSource: 'lead-capture-form',
          userId // This will be either authenticated user's ID or card owner's ID
        },
        cardData,
        authState?.token || null // Pass token if available, null if not authenticated
      );

      const leadId = typeof leadResponse === 'string' ? leadResponse : (leadResponse._id || 'unknown');

      if (tracking) {
        tracking.trackLeadCapture({
          leadId,
          leadName,
          leadEmail,
          leadSource: 'lead-capture-form',
          cardId: cardId,
          timestamp: new Date().toISOString(),
          isAuthenticated
        });
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving lead:', error);
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.message;
        if (errorMessage.includes('email')) {
          setLeadEmailError(errorMessage);
        } else if (errorMessage.includes('name')) {
          setLeadNameError(errorMessage);
        } else if (errorMessage.includes('phone')) {
          setLeadPhoneNumberError(errorMessage);
        }
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    leadName, leadEmail, leadPhoneNumber, leadJobTitle, leadCompany, leadNotes,
    cardId, userId, validateForm, cardUrl, cardEmail, authState?.token,
    tracking, onSuccess, onClose, isAuthenticated
  ]);

  // Fetch card details - STABLE with single execution
  useEffect(() => {
    let isMounted = true;

    const fetchCardDetails = async () => {
      if (!cardId) return;

      try {
        const data = await getCardById(cardId);
        if (isMounted) {
          setCardDetails(data);
          setLoading(false);
          // Update input fields config immediately when card data is fetched
          if (data?.card?.leadCaptureInputFields) {
            const fieldsWithType = data.card.leadCaptureInputFields.map(f => ({ ...f, type: f.type || 'text' }));
            setInputFieldsConfig(fieldsWithType);
            
            if (process.env.NODE_ENV === 'development') {
              console.log('🔍 Overlay: Fetched from server', fieldsWithType);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching card details:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCardDetails();

    return () => {
      isMounted = false;
    };
  }, [cardId]);

  // ✅ CRITICAL: Prioritize card context data (real-time updates) over server data
  useEffect(() => {
    let fieldsToUse = null;
    let source = 'none';

    // First check card context (has real-time updates)
    if (cardId && cardState[cardId]?.leadCaptureInputFields) {
      const contextFields = cardState[cardId].leadCaptureInputFields;
      if (contextFields && Array.isArray(contextFields) && contextFields.length > 0) {
        fieldsToUse = contextFields.map(f => ({ ...f, type: f.type || 'text' }));
        source = 'context';
      }
    }
    
    // Fallback to server data if context doesn't have it
    if (!fieldsToUse && cardDetails?.card?.leadCaptureInputFields) {
      const serverFields = cardDetails.card.leadCaptureInputFields;
      if (serverFields && Array.isArray(serverFields) && serverFields.length > 0) {
        fieldsToUse = serverFields.map(f => ({ ...f, type: f.type || 'text' }));
        source = 'server';
      }
    }

    if (fieldsToUse) {
      setInputFieldsConfig(fieldsToUse);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Overlay: Updated fields from', source, {
          cardId,
          fieldsCount: fieldsToUse.length,
          fields: fieldsToUse.map(f => ({ label: f.label, type: f.type, required: f.required }))
        });
      }
    }
  }, [cardId, cardState, cardDetails]);

  // Set card info - STABLE
  useEffect(() => {
    if (cardDetails?.card && !cardFullName) {
      setCardFullName(cardDetails.card.contact_info?.fullName || '');
      setCardProfilePictureUrl(cardDetails.card.profilePictureUrl || '');
      setCardEmail(cardDetails.card.contact_info?.email || '');
    }
  }, [cardDetails?.card, cardFullName]);

  // Don't render if we don't have essential data
  if (!userId && !loading) {
    console.warn('Lead capture overlay: No user ID available');
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={handleBackdropClick}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
        }}
      />

      {/* Overlay */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: { xs: '95%', sm: '90%', md: '400px' },
          maxWidth: '400px',
          margin: '0 auto',
          backgroundColor: '#fff',
          padding: { xs: '16px', sm: '20px', md: '24px' },
          borderRadius: { xs: '16px', sm: '20px' },
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          position: 'fixed',
          left: '50%',
          zIndex: 10000,
          ...MODAL_POSITION,
          overflow: 'visible',
          maxHeight: showMoreFields ? { xs: '98vh', sm: '95vh', md: '90vh' } : { xs: '85vh', sm: '80vh', md: '75vh' },
          transition: 'top 0.3s ease-in-out', // Smooth transition when position changes
          '@media (max-width: 600px)': {
            margin: '0 8px',
            width: 'calc(100% - 16px)',
            maxWidth: 'calc(100% - 16px)',
          }
        }}
      >
        {/* Profile Picture */}
        {cardProfilePictureUrl && (
          <Avatar
            src={cardProfilePictureUrl}
            alt="Profile Picture"
            sx={{
              width: { xs: 70, sm: 80, md: 90 },
              height: { xs: 70, sm: 80, md: 90 },
              position: 'absolute',
              top: {
                xs: '-35px',
                sm: '-40px',
                md: '-45px'
              },
              left: '50%',
              transform: 'translateX(-50%)',
              border: { xs: '3px solid white', sm: '4px solid white' },
              boxShadow: '0 6px 15px rgba(0, 0, 0, 0.3)',
              zIndex: 10001,
            }}
          />
        )}

        <Box sx={{
          marginTop: { xs: '40px', sm: '50px', md: '60px' },
          px: { xs: 0, sm: 1 }
        }}>
          <Typography
            variant="body2"
            sx={{
              marginBottom: { xs: 1.5, sm: 2 },
              fontWeight: '700',
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
              textAlign: 'center',
              lineHeight: 1.3
            }}
          >
            Share your info with {cardFullName}
          </Typography>

          <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

          {/* Email Field */}
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            name="email"
            value={leadEmail}
            onChange={handleInputChange}
            error={Boolean(leadEmailError)}
            helperText={leadEmailError}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />

          {/* Name Field */}
          <TextField
            label="Full Name"
            variant="outlined"
            fullWidth
            name="name"
            value={leadName}
            onChange={handleInputChange}
            error={Boolean(leadNameError)}
            helperText={leadNameError}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />

          {/* Phone Number Field */}
          <PhoneInputField
            value={leadPhoneNumber}
            onChange={handlePhoneChange}
            error={leadPhoneNumberError}
            country="sg"
            containerSx={{ mb: 1 }}
          />

          {/* Add More Button */}
          {!showMoreFields && (
            <Button
              onClick={handleAddMoreClick}
              sx={{
                color: addMoreConnectButtonColor,
                fontWeight: 'bold',
                backgroundColor: 'transparent',
                textTransform: 'none',
                marginBottom: { xs: '12px', sm: '16px' },
                display: 'flex',
                alignItems: 'center',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '8px 12px', sm: '10px 16px' },
              }}
            >
              <AddIcon sx={{
                marginRight: '5px',
                color: addMoreConnectButtonColor,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }} />
              ADD MORE
            </Button>
          )}

          {/* Additional Fields */}
          {showMoreFields && (
            <>
              {fieldExists('Job Title') && (
                <TextField
                  label={getFieldLabel('Job Title')}
                  variant="outlined"
                  fullWidth
                  name="jobTitle"
                  value={leadJobTitle}
                  onChange={handleInputChange}
                  required={isFieldRequired('Job Title')}
                  sx={{ mb: 1, mt: 1 }}
                />
              )}

              {fieldExists('Company') && (
                <TextField
                  label={getFieldLabel('Company')}
                  variant="outlined"
                  fullWidth
                  name="company"
                  value={leadCompany}
                  onChange={handleInputChange}
                  required={isFieldRequired('Company')}
                  sx={{ mb: 1 }}
                />
              )}

              {fieldExists('Note') && (
                <TextField
                  label={getFieldLabel('Note')}
                  variant="outlined"
                  fullWidth
                  name="notes"
                  value={leadNotes}
                  onChange={handleInputChange}
                  required={isFieldRequired('Note')}
                  multiline
                  rows={3}
                  sx={{ mb: 1 }}
                />
              )}

              {/* Render additional dynamic text fields */}
              {getAdditionalFields().length > 0 && (
                <>
                  {getAdditionalFields().map((field, index) => (
                    <TextField
                      key={`dynamic-${index}-${field.label}`}
                      label={field.label}
                      variant="outlined"
                      fullWidth
                      value={dynamicFieldValues[field.label] || ''}
                      onChange={(e) => handleDynamicFieldChange(field.label, e.target.value)}
                      error={Boolean(dynamicFieldErrors[field.label])}
                      helperText={dynamicFieldErrors[field.label]}
                      required={field.required}
                      multiline={field.label.toLowerCase().includes('note') || field.label.toLowerCase().includes('message')}
                      rows={field.label.toLowerCase().includes('note') || field.label.toLowerCase().includes('message') ? 3 : 1}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </>
              )}
            </>
          )}


          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting || !userId}
            sx={{
              marginTop: { xs: 1.5, sm: 2 },
              textTransform: 'none',
              borderRadius: '20px',
              padding: { xs: '12px 20px', sm: '14px 20px', md: '16px 20px' },
              fontWeight: 'bold',
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              marginBottom: '4px',
              backgroundColor: addMoreConnectButtonColor,
              '&:hover': {
                backgroundColor: darken(addMoreConnectButtonColor, 0.2),
              },
              '&:disabled': {
                backgroundColor: darken(addMoreConnectButtonColor, 0.3),
              }
            }}
          >
            {submitting ? 'Connecting...' : 'Connect'}
          </Button>

          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              marginTop: { xs: 1.5, sm: 2 },
              fontWeight: 'bold',
              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
              color: '#666666',
              lineHeight: 1.3,
              px: { xs: 1, sm: 0 }
            }}
          >
            Touch does not sell or share your data
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default LeadCaptureOverlay;